import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import { loadManagementAPI, LoadAlert } from '@/services/api';

interface LoadAlertsWidgetProps {
  alerts?: LoadAlert[];
}

const LoadAlertsWidget: React.FC<LoadAlertsWidgetProps> = ({ alerts: prefetchedAlerts }) => {
  const [fetchedAlerts, setFetchedAlerts] = useState<LoadAlert[]>([]);
  const [loading, setLoading] = useState(prefetchedAlerts === undefined);
  const [error, setError] = useState<string | null>(null);
  const alerts = prefetchedAlerts ?? fetchedAlerts;

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      setFetchedAlerts((await loadManagementAPI.getLoadAlerts()) ?? []);
      setError(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load load management alerts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (prefetchedAlerts !== undefined) {
      return;
    }

    void fetchAlerts();

    // Refresh alerts every 5 minutes
    const interval = setInterval(() => void fetchAlerts(), 5 * 60 * 1000);
    return () => clearInterval(interval);
    // Prefetched data controls whether this component owns the request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefetchedAlerts]);

  const getRecommendation = (riskCategory: string) => riskCategory === 'very_high'
    ? 'Consider immediate load reduction and medical assessment.'
    : 'Monitor closely and consider reducing training intensity.';

  const getAlertIcon = (riskCategory: string) => {
    if (riskCategory === 'very_high') {
      return (
        <svg aria-hidden="true" className="h-5 w-5 text-red-500 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      );
    }
    return (
      <svg aria-hidden="true" className="h-5 w-5 text-orange-500 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    );
  };

  const getAlertBgColor = (riskCategory: string) => {
    return riskCategory === 'very_high'
      ? 'bg-red-50 border-red-200 dark:bg-red-950/40 dark:border-red-800'
      : 'bg-orange-50 border-orange-200 dark:bg-orange-950/40 dark:border-orange-800';
  };

  const getAlertTextColor = (riskCategory: string) => {
    return riskCategory === 'very_high' ? 'text-red-800 dark:text-red-200' : 'text-orange-800 dark:text-orange-200';
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div role="status" aria-label="Loading load alerts" className="animate-pulse motion-reduce:animate-none motion-reduce:transition-none">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Load Management Alerts</h3>
          <p className="text-sm text-gray-600 dark:text-gray-300">{alerts.length} player{alerts.length !== 1 ? 's' : ''} require attention</p>
        </div>
        <Link
          to="/players"
          className="inline-flex min-h-11 items-center text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 text-sm font-medium transition-colors motion-reduce:transition-none"
        >
          View All →
        </Link>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 dark:bg-red-950/40 dark:border-red-800 rounded-md p-3 mb-4">
          <p className="text-sm text-red-600 dark:text-red-300">{error}</p>
          <Button variant="outline" size="sm" className="mt-2 min-h-11 motion-reduce:transition-none" onClick={() => void fetchAlerts()}>
            Retry load alerts
          </Button>
        </div>
      )}

      <div className="space-y-4">
        {alerts.length === 0 && !error && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/60">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">No load alerts</h4>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              No players currently need load-management attention.
            </p>
          </div>
        )}
        {alerts.map((alert) => (
          <div
            key={alert.playerId}
            className={`rounded-lg border p-4 ${getAlertBgColor(alert.riskCategory)}`}
          >
            <div className="flex items-start">
              <div className="flex-shrink-0 mr-3 mt-0.5">
                {getAlertIcon(alert.riskCategory)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h4 className={`text-sm font-medium ${getAlertTextColor(alert.riskCategory)}`}>
                      {alert.name}
                    </h4>
                    <p className="text-xs text-gray-600 dark:text-gray-300">{alert.position}</p>
                  </div>
                  <div className="text-right">
                    <div className={`text-sm font-semibold ${getAlertTextColor(alert.riskCategory)}`}>
                      ACWR: {alert.acwr == null ? '—' : alert.acwr.toFixed(2)}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-300">
                      {alert.riskCategory === 'very_high' ? 'Critical' : 'High'} Risk
                    </div>
                  </div>
                </div>
                <p className="text-sm text-gray-700 dark:text-gray-200 mb-3">{getRecommendation(alert.riskCategory)}</p>
                <div className="flex space-x-2">
                  <Link
                    to={`/players/${alert.playerId}`}
                    className="inline-flex min-h-11 items-center rounded-md border border-gray-300 dark:border-gray-600 px-3 text-sm font-medium text-gray-700 dark:text-gray-100 transition-colors hover:bg-gray-50 dark:hover:bg-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 motion-reduce:transition-none"
                  >
                    View Player
                  </Link>
                  <Link
                    to={`/players/${alert.playerId}`}
                    className="inline-flex min-h-11 items-center rounded-md border border-gray-300 dark:border-gray-600 px-3 text-sm font-medium text-gray-700 dark:text-gray-100 transition-colors hover:bg-gray-50 dark:hover:bg-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 motion-reduce:transition-none"
                  >
                    Load Analysis
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {alerts.length > 3 && (
        <div className="text-center mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Link
            to="/players"
            className="inline-flex min-h-11 items-center text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium transition-colors motion-reduce:transition-none"
          >
            View all {alerts.length} alerts →
          </Link>
        </div>
      )}
    </Card>
  );
};

export default LoadAlertsWidget;
